import { useEffect, useMemo, useState } from 'preact/hooks';
import { ChipCount } from '../components/ChipCount';
import {
  filterPos,
  LISTED_PO_STATUSES,
  summarisePos,
  type ByCurrency,
  type ListedPoStatus,
  type PoListBody,
} from '../../shared/po/list';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet } from '../lib/api';
import { formatDate } from '../lib/dates';

// Purchase orders register (D71): every numbered PO of a year, Procurement and CFO.

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; forbidden: boolean; reference: string | null }
  | { kind: 'ok'; data: PoListBody };

const STATUS_TONE: Record<ListedPoStatus, string> = {
  pendingCFO: 'info',
  pendingVertical: 'info',
  sentPendingCFO: 'warning',
  approved: 'success',
  rejected: 'neutral',
};
const DELIVERY_TONE: Record<string, string> = {
  none: 'neutral',
  partial: 'warning',
  full: 'success',
  notRequired: 'neutral',
};
const FIRST_YEAR = 2026;
/** Tiles: whole units with k / M, tables always in full (D102). */
const short = (minor: number) => {
  const v = minor / 100;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 10_000) return `${(v / 1000).toFixed(1)}k`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v);
};
const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

export function PurchaseOrdersPage() {
  const { t, lang } = useI18n();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [status, setStatus] = useState<ListedPoStatus | 'all'>('all');
  const [entity, setEntity] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    let live = true;
    setLoad({ kind: 'loading' });
    apiGet<PoListBody>(`/api/pos?year=${year}`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({
          kind: 'error',
          forbidden: e?.code === 'forbidden',
          reference: e?.correlationId ?? null,
        });
      });
    return () => {
      live = false;
    };
  }, [year]);

  const rows = load.kind === 'ok' ? load.data.rows : [];
  const entities = useMemo(() => [...new Set(rows.map((r) => r.entity))].sort(), [rows]);
  const shown = filterPos(rows, { status, entity, q });
  const count = (s: ListedPoStatus | 'all') =>
    s === 'all' ? rows.length : rows.filter((r) => r.status === s).length;
  const years: number[] = [];
  for (let y = thisYear; y >= FIRST_YEAR; y--) years.push(y);

  const sum = summarisePos(shown);
  const amounts = (list: ByCurrency) =>
    list.length === 0 ? '—' : list.map((x) => `${x.currency} ${short(x.amount)}`).join(' · ');

  return (
    <div class="stack">
      <div class="page-actions">
        <p class="caption">{t('pol.lead')}</p>
        <a class="button button--small" href="#/prices">
          {t('ph.open')}
        </a>
      </div>
      {load.kind === 'ok' && (
        <ul class="tiles" aria-label={t('pol.tiles')}>
          <li class="tile">
            <span class="caption">{t('pol.tile.value', { n: String(sum.count) })}</span>
            <span class="tile__value" dir="ltr">
              {amounts(sum.value)}
            </span>
          </li>
          <li class="tile">
            <span class="caption">{t('pol.tile.invoiced')}</span>
            <span class="tile__value" dir="ltr">
              {amounts(sum.invoiced)}
            </span>
          </li>
          <li class={`tile${sum.withCfo > 0 ? ' tile--warning' : ''}`}>
            <span class="caption">{t('pol.tile.withCfo')}</span>
            <span class="tile__value">{sum.withCfo}</span>
          </li>
          <li class={`tile${sum.toDeliver > 0 ? ' tile--warning' : ''}`}>
            <span class="caption">{t('pol.tile.toDeliver')}</span>
            <span class="tile__value">{sum.toDeliver}</span>
          </li>
        </ul>
      )}
      <div class="admin-tabs" role="group" aria-label={t('pol.statusFilter')}>
        {(['all', ...LISTED_PO_STATUSES] as const).map((st) => (
          <button
            key={st}
            type="button"
            class="chip"
            aria-pressed={status === st}
            onClick={() => setStatus(st)}
          >
            {t(st === 'all' ? 'pol.all' : (`cfo.poStatus.${st}` as MessageKey))}
            {load.kind === 'ok' && <ChipCount n={count(st)} />}
          </button>
        ))}
      </div>
      <section class="card stack" aria-labelledby="pol-title">
        <div class="card__head">
          <h2 id="pol-title" class="card__title">
            {t('pol.listTitle', { year: String(year) })}
          </h2>
          {load.kind === 'ok' && (
            <span class="caption">
              {t('pol.showing', { n: String(shown.length), total: String(rows.length) })}
            </span>
          )}
        </div>
        <div class="queue-filters">
          <div class="field">
            <label for="pol-q">{t('pol.search')}</label>
            <input
              id="pol-q"
              type="search"
              value={q}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label for="pol-entity">{t('pol.entity')}</label>
            <select
              id="pol-entity"
              value={entity}
              onChange={(e) => setEntity((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('pol.allEntities')}</option>
              {entities.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>
          <div class="field">
            <label for="pol-year">{t('pol.year')}</label>
            <select
              id="pol-year"
              value={String(year)}
              onChange={(e) => setYear(Number((e.target as HTMLSelectElement).value))}
            >
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p>{t(load.forbidden ? 'review.error.forbidden' : 'pol.loadError')}</p>
            {load.reference && (
              <p class="caption" dir="ltr">
                {t('error.reference', { ref: load.reference })}
              </p>
            )}
          </div>
        )}
        {load.kind === 'ok' &&
          (shown.length === 0 ? (
            <p>{t('pol.none')}</p>
          ) : (
            <div class="table-wrap">
              <table class="table table--zebra pol-table">
                <thead>
                  <tr>
                    <th scope="col">{t('pol.col.po')}</th>
                    <th scope="col">{t('pol.col.requester')}</th>
                    <th scope="col">{t('inv.col.supplier')}</th>
                    <th scope="col" class="num">
                      {t('pol.col.total')}
                    </th>
                    <th scope="col">{t('pol.col.status')}</th>
                    <th scope="col">{t('pol.col.delivery')}</th>
                    <th scope="col">{t('pol.col.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r, i) => {
                    const pct =
                      r.total > 0 ? Math.min(100, Math.round((r.invoiced / r.total) * 100)) : 0;
                    return (
                      <tr key={r.poNo || `${r.prNo}-${r.status}-${i}`}>
                        <td>
                          {/* One link per row: the request, where the PO document opens (D95). */}
                          <a
                            class="row-ref"
                            href={`#/requests/${r.prNo}`}
                            dir={r.poNo ? 'ltr' : undefined}
                          >
                            {r.poNo || t('pol.noNumber')}
                          </a>
                          {r.title && (
                            <span class="pol-title" dir="auto">
                              {r.title}
                            </span>
                          )}
                          <span class="caption cell-sub">
                            <span dir="ltr">{r.prNo}</span>
                            {` · ${r.entity}`}
                            {r.vertical ? ` · ${r.vertical}` : ''}
                          </span>
                        </td>
                        <td>
                          {r.requesterName || '—'}
                          {r.department && <span class="caption cell-sub">{r.department}</span>}
                        </td>
                        <td dir="auto">{r.supplierName || '—'}</td>
                        <td class="num" dir="ltr">
                          <span class="pol-total">
                            {r.currency} {money(r.total)}
                          </span>
                          {(r.status === 'approved' || r.status === 'sentPendingCFO') &&
                            r.total > 0 && (
                              <span class="pol-inv">
                                <span
                                  class="pol-inv__bar"
                                  role="img"
                                  aria-label={t('pol.invoicedPct', { pct: String(pct) })}
                                >
                                  <span style={{ width: `${pct}%` }} />
                                </span>
                                <span class="caption">
                                  {t('pol.invoicedPct', { pct: String(pct) })}
                                </span>
                              </span>
                            )}
                        </td>
                        <td>
                          <span class={`pill pill--${STATUS_TONE[r.status]}`}>
                            {t(`cfo.poStatus.${r.status}` as MessageKey)}
                          </span>
                        </td>
                        <td>
                          <span class={`pill pill--${DELIVERY_TONE[r.delivery] ?? 'neutral'}`}>
                            {t(`pol.delivery.${r.delivery}` as MessageKey)}
                          </span>
                        </td>
                        <td>{r.date ? formatDate(r.date, lang) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </div>
  );
}
